import {
  useEffectWithController,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import PickupAndDeliveryOption from '~/constants/common/PickupAndDeliveryOption';
import PurchaseType from '~/constants/purchaseType';
import { VehicleLockTypeKey } from '~/constants/vehicleLockType';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import {
  useFavoritesIds,
  useIsLoggedIn,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import LocationContext from '~/context/vdp/LocationContext';
import VehiclesAvailableSoonerContext from '~/context/vdp/VehiclesAvailableSoonerContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { getBulkAvailabilityInfo } from '~/hooks/vdp/services/index';
import {
  getDeliveryInfo,
  getDeliveryPickup,
  getPickupInfo,
  getSearchRequestId,
  getVehicleDetails
} from '~/selectors';
import { fetchRecommendations } from '~/services/vdp';
import useSelector from '~/store/useSelector';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

const oneDay = 1000 * 60 * 60 * 24;

const getOneDayEarlier = (delivery: number, pickup: number) => {
  if (!delivery && !pickup) return 0;
  const dates = [delivery, pickup].filter(date => date > 0);
  const earliestDate = Math.min(...dates);
  const msUntilDelivery = earliestDate - new Date().getTime();

  // flooring (contra rounding) ensures that we never show
  // vehicles when VDP's earliest delivery/pickup is tomorrow
  return Math.floor(msUntilDelivery / oneDay) - 1;
};

const getBestSearchResults = (array: RecommendedVehicle[]) =>
  array.sort((a, b) => {
    const {
      price: { total: aPrice }
    } = a;
    const {
      price: { total: bPrice }
    } = b;
    const aDays = a.earliestDaysToAvailability ?? 0;
    const bDays = b.earliestDaysToAvailability ?? 0;

    return aDays - bDays || aPrice - bPrice;
  });

function useGetVehiclesAvailableSooner(shouldLoad: boolean) {
  const {
    deliveryContext: { isLoaded: isDeliveryContextLoaded }
  } = useContext(LocationContext);
  const { setData, setError, setLoading } = useContext(
    VehiclesAvailableSoonerContext
  );
  const [localVehicles, setLocalVehicles] = useState<RecommendedVehicle[]>([]);
  const favoritesIds = useFavoritesIds();
  const mounted = usePromise();
  const searchRequestId = useSelector(getSearchRequestId);
  const isMobile = useIsMobile();
  const { zip } = useUserLocation();
  const { browserName } = useContext(DeviceContext);
  const { clientId, eventCorrelationId } = useContext(StaticContext);
  const earliestDaysToDelivery = getOneDayEarlier(
    useSelector(getDeliveryInfo)?.utcSoonestDateTime ?? 0,
    useSelector(getPickupInfo)?.utcSoonestDateTime ?? 0
  );
  const isLoggedIn = useIsLoggedIn();
  const userId = useUserId();
  const { referrer } = useContext(StaticContext);
  const {
    make = '',
    model = '',
    vehicleId = 0,
    mileage = 0,
    year = 0
  } = useSelector(getVehicleDetails);

  // We won't take action based on the zip code changing because we listen for
  // other changes (delivery/pickup & delivery context) that trigger when the
  // zip code changes.
  const zipRef = useRef(zip);
  useEffect(() => {
    zipRef.current = zip;
  }, [zip]);

  const shouldLoadData =
    useSelector(getDeliveryPickup).isLoaded &&
    isDeliveryContextLoaded &&
    shouldLoad;

  useEffectWithController(
    controller => {
      if (!shouldLoadData) {
        return;
      }

      if (earliestDaysToDelivery < 1) {
        setData([]);

        return;
      }

      async function fetchVehicles() {
        setLoading();
        const vehicles = await fetchRecommendations(
          {
            analyticsData: {
              correlationId: eventCorrelationId,
              searchRequestId,
              isMobileDevice: isMobile,
              browser: browserName,
              filterSource: 'notSpecified',
              referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            clientId,
            filters: {
              earliestDayToDeliveryOrPickup: {
                pickupAndDeliveryOption:
                  PickupAndDeliveryOption.PickupAndDelivery,
                days: earliestDaysToDelivery
              },
              lockTypes: [VehicleLockTypeKey.None],
              makes: [
                {
                  name: make,
                  models: [
                    {
                      name: model
                    }
                  ]
                }
              ],
              mileage: { max: Math.floor(mileage * 1.5) },
              purchaseTypes: [PurchaseType.Purchasable],
              year: { min: year - 2 }
            },
            numVehiclesToReturn: 18,
            vehicleId,
            zip5: zipRef.current
          },
          isLoggedIn,
          controller
        );

        if (vehicles.length === 0) return [];

        const deliveryData = await getBulkAvailabilityInfo({
          clientId,
          correlationId: eventCorrelationId,
          userId,
          vehicles,
          zip: zipRef.current
        });

        return vehicles.map(vehicle => ({
          ...vehicle,
          ...deliveryData.find(
            vehicleData => vehicleData.vehicleId === vehicle.vehicleId
          )
        }));
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setLocalVehicles(getBestSearchResults(vehicles));
          if (vehicles.length === 0) setData([]);
        })
        .catch(
          generateErrorLogger(
            'Get recommended vehicles',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      browserName,
      clientId,
      earliestDaysToDelivery,
      eventCorrelationId,
      isLoggedIn,
      isMobile,
      make,
      mileage,
      model,
      mounted,
      referrer,
      searchRequestId,
      setData,
      setError,
      setLoading,
      shouldLoadData,
      userId,
      vehicleId,
      year
    ]
  );

  useEffect(() => {
    if (localVehicles.length === 0) return;

    setData(
      localVehicles.map<RecommendedVehicle>(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
  }, [favoritesIds, localVehicles, setData]);
}

export default useGetVehiclesAvailableSooner;
